import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router-dom';
import { SideManu } from '../components';
import {
  APP_FEATURES,
  APP_ROUTES,
  RESTRICTED_ALLOWED_ROUTES,
  SIDE_BAR_ITEMS,
  SIDE_MENU_BOTTOM_ITEMS,
} from '../constants';
import SubscriptionLockScreen from '../components/SubscriptionLockScreen';
import { useSubscriptionLock } from '../hooks/useSubscriptionLock';
import { ISideBarItem } from '../interface/main.interface';
import Navbar from './../components/TopBbar';
import GracePeriodBanner, { GRACE_PERIOD_SEVERITY } from '../components/GracePeriodBanner';
import { useGracePeriodStatus } from '../hooks/useGracePeriodStatus';
import { useSubscriptionPolling } from '../hooks/useSubscriptionPolling';
import { hasPrivilege } from '../utility/hasPrivilege';
// import AgentFAB from '../components/agent/AgentFAB';
// import AgentDrawer from '../components/agent/AgentDrawer';
import '../styles/design-tokens.css';
import '../styles/animations.css';

// Custom route titles for routes not in sidebar
const CUSTOM_ROUTE_TITLES: Record<string, string> = {
  // CVA-4197 plan 0011 cleanup (2026-09-03) — the dormant `/exam-marking`
  // list page (and its "Exam Marking" sidebar tree node, kept only for this
  // title lookup) were removed; this still-live subject-marking screen needs
  // its title from somewhere.
  [APP_ROUTES.EXAM_SUBJECT_MARKING]: 'Mark Exam Subject',
  // Student/guardian exam detail and report card aren't sidebar children
  // (STUDENT_EXAMS's only child is the report card, which doesn't cover the
  // detail route either) — both need a title from here or the header renders
  // an empty <h1>.
  [APP_ROUTES.STUDENT_EXAM_DETAIL]: 'Exam Details',
  [APP_ROUTES.STUDENT_EXAM_REPORT_CARD]: 'Report Card',
};

// Same derivation the sidebar uses, so the header title translates with the same keys
const menuKey = (label?: string): string =>
  !label
    ? ''
    : 'menu.' +
      label
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');

/** Sidebar label for a path, searching nested items. Pure: depends only on its arguments. */
function findCurrentRouteLabel(currentPath: string, items: ISideBarItem[]): string | null {
  for (const item of items) {
    if (item.route) {
      const routePattern = new RegExp(`^${item.route.replace(/:\w+/, '[^/]+')}$`);
      if (routePattern.test(currentPath)) return item.label;
    }
    if (item.children) {
      const childLabel = findCurrentRouteLabel(currentPath, item.children);
      if (childLabel) return childLabel;
    }
  }
  return null;
}

const MainLayout: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const [isMenuPinned, setIsMenuPinned] = useState(true); // Pinned open by default
  const [isHovering, setIsHovering] = useState(false);

  // Initialize centralized subscription polling (single timer for entire app)
  useSubscriptionPolling();

  // Get grace period status from Redux (no polling, just reading state)
  const { status: gracePeriodStatus, loading: gracePeriodLoading } = useGracePeriodStatus();

  // Subscription lockout (CVA-4668). The API decides — trial ended, cancelled
  // past the paid period, unpaid past grace, and every exemption — via
  // GET /subscription/lockout-status and any 402 it returns.
  const subscriptionLock = useSubscriptionLock();
  // Routes that stay reachable while locked out, so an organization can still
  // sign in and pay. /home is NOT among them: it was previously exempt, which
  // left the post-login landing page completely unblocked.
  const isLockAllowedRoute = RESTRICTED_ALLOWED_ROUTES.some(
    (route) => location.pathname === route || location.pathname.startsWith(`${route}/`),
  );
  const showLockScreen = subscriptionLock.locked && !isLockAllowedRoute;
  // Hold the route until the lockout answer is known. Rendering the Outlet
  // first meant a locked-out organization briefly got the real page — it
  // mounted and fired its API calls before the lock screen replaced it.
  const awaitingLockDecision = !subscriptionLock.resolved && !isLockAllowedRoute;
  // Subscription payments are handled by admins only (CVA-4387) — the
  // grace-period banner stays hidden for every other role. The lock screen
  // above uses this too, to decide between "pay now" and "contact your
  // administrator" (CVA-3661).
  const canManageBilling = hasPrivilege(APP_FEATURES.BILLING_MANAGEMENT);

  const handleMenuClick = () => {
    setIsMenuPinned((prev) => !prev);
  };

  useEffect(() => {
    const checkScreenSize = () => {
      if (window.innerWidth < 1280) {
        setIsMenuPinned(false);
      } else {
        setIsMenuPinned(true);
      }
    };

    checkScreenSize();

    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const isMenuExpanded = isMenuPinned || isHovering;

  const pageTitle = useMemo(() => {
    // First check custom route titles for dynamic routes
    for (const [route, title] of Object.entries(CUSTOM_ROUTE_TITLES)) {
      const routePattern = new RegExp(`^${route.replace(/:\w+/g, '[^/]+')}$`);
      if (routePattern.test(location.pathname)) {
        return t(menuKey(title), title);
      }
    }
    // Then check the main sidebar items, falling back to the bottom "Profile"
    // group (My Institute, My Profile, Change Password) — those routes are
    // sidebar-driven too, just via a second array, and were previously never
    // matched here, so their header title silently rendered blank.
    const label =
      findCurrentRouteLabel(location.pathname, SIDE_BAR_ITEMS) ||
      findCurrentRouteLabel(location.pathname, SIDE_MENU_BOTTOM_ITEMS);
    return label ? t(menuKey(label), label) : '';
  }, [location.pathname, t]);

  return (
    <div className="flex h-screen bg-[#F5F5F5] overflow-hidden">
      {/* Sidebar - Full Height */}
      <div
        className="h-full fixed top-0 left-0 z-20 flex-shrink-0"
        onMouseEnter={() => {
          if (!isMenuPinned) setIsHovering(true);
        }}
        onMouseLeave={() => setIsHovering(false)}
      >
        <div
          className={`h-full transition-all duration-300 ease-in-out flex-shrink-0 ${
            isMenuExpanded ? 'w-72' : 'w-20'
          }`}
        >
          <SideManu isExpanded={isMenuExpanded} isPinned={isMenuPinned} />
        </div>
      </div>

      {/* Right Side: Header + Content + Footer */}
      <div
        className={`flex flex-col flex-1 h-full overflow-hidden transition-all duration-300 ease-in-out ${
          isMenuPinned ? 'xl:ml-72 ml-20' : 'ml-20'
        }`}
      >
        {/* Header */}
        <div className="flex-shrink-0">
          <Navbar title={pageTitle} handleMenuClick={handleMenuClick} />
        </div>

        {/* Content Area */}
        <div
          id="scrollable-outlet"
          className="flex-1 overflow-y-auto overflow-x-hidden p-4 min-h-0"
        >
          {/* Subscription Status Banner — single banner, single source of truth.
              Shown on ALL routes, including the common Home landing, so admins
              whose org is in grace/expired still see their billing status there.
              (Informational only — the hard-block overlay stays bypassed on /home.)
              Admin-only (CVA-4387): non-admin roles (Teacher/Parent/Student/etc.)
              can't act on an invoice, so they never see this banner. */}
          {!gracePeriodLoading &&
            gracePeriodStatus &&
            canManageBilling &&
            gracePeriodStatus.isRestricted === false &&
            (gracePeriodStatus.isInGracePeriod || gracePeriodStatus.unpaidInvoiceCount > 0) && (
              <div className="pb-4">
                <GracePeriodBanner
                  isRestricted={gracePeriodStatus.isRestricted}
                  isInGracePeriod={gracePeriodStatus.isInGracePeriod}
                  unpaidInvoiceCount={gracePeriodStatus.unpaidInvoiceCount}
                  showViewPlansButton={location.pathname !== APP_ROUTES.BILLING_AND_PLANS}
                  severity={gracePeriodStatus.severity as GRACE_PERIOD_SEVERITY}
                  gracePeriodEndDate={
                    gracePeriodStatus.gracePeriodEndDate
                      ? new Date(gracePeriodStatus.gracePeriodEndDate).toISOString()
                      : undefined
                  }
                  daysRemaining={gracePeriodStatus.daysRemaining}
                  outstandingAmount={gracePeriodStatus.outstandingAmount}
                />
              </div>
            )}

          {/* The lock screen REPLACES the route. It is never drawn on top of it:
              the previous overlay left the real page mounted underneath, so its
              data still loaded and stayed readable through the blur. */}
          <div className="relative max-w-full min-h-full">
            {awaitingLockDecision ? (
              <div
                data-testid="subscription-lock-pending"
                className="flex min-h-[60vh] items-center justify-center"
              >
                <i className="pi pi-spin pi-spinner text-3xl text-gray-400" aria-hidden="true" />
              </div>
            ) : showLockScreen ? (
              <SubscriptionLockScreen
                code={subscriptionLock.code}
                message={subscriptionLock.message}
                details={subscriptionLock.details}
                canManageBilling={canManageBilling}
              />
            ) : (
              <Outlet />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-[#3E3E3E] w-full py-3 px-6 text-white flex-shrink-0 flex items-center justify-center">
          <span className="text-sm font-medium">www.classveew.com</span>
        </div>
      </div>

      {/* AI Marketing Agent — moved to full-page route /classveew-ai */}
      {/* <AgentFAB /> */}
      {/* <AgentDrawer /> */}
    </div>
  );
};

export default MainLayout;
